/* eslint-disable react/jsx-no-duplicate-props */
/* eslint-disable no-unused-vars */
import { useEffect, useMemo } from 'react';

import { Button, DatePicker, Modal, Popover, Tooltip } from 'antd';
import FeatherIcon from 'feather-icons-react/build/FeatherIcon';
import queryString from 'query-string';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { generateQueryString, ResponsiveComponent } from '../../utils/functions';
import Sidebar from '../../components/Sidebar';
import { CustomTable } from '../../components/Table';
import { PrakurtiCirculerLogo, refreshicon } from '../../components/imagepath';
import Header from '../../components/Header';
import { ActionGetAllMedicalDetail } from '../../store/Actions/MedicalStoreAction';
import { matchPermissions } from '../../utils/Permission';
import { PERMISSIONS } from '../../constants';
import TableColumnSearch1 from '../../components/Input/TableColumnSearch1';
import dayjs from 'dayjs';

const MedicalStore = () => {
  const [showArrow, setShowArrow] = useState(true);
  const [Refresh, setRefresh] = useState(false);
  const [arrowAtCenter, setArrowAtCenter] = useState(false);
  const [IsSearching, setIsSearching] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');
  const [SearchList, setSearchList] = useState([]);

  const { loader, medicalStore } = useSelector(state => state?.MedicalStore);
  const { userDetails } = useSelector(state => state?.auth);

  const Size = ResponsiveComponent();
  const pageSize = 10;
  const dispatch = useDispatch();
  const location = useLocation();
  const url = new URL(window.location.href);
  const searchParams = url.searchParams;
  const params = queryString.parse(location.search);
  const nameParams = searchParams.get('Name');

  const [Query, setQuery] = useState({
    page: searchParams.get('page'),
    name: nameParams,
    medicalStoreStartDate: '',
    medicalStoreEndDate: '',
  });

  // Permission checks
  const matchPermissionsForUser = matchPermissions(userDetails);
  const canView = matchPermissionsForUser(PERMISSIONS.MEDICAL_STORE.VIEW);
  const canCreate = matchPermissionsForUser(PERMISSIONS.MEDICAL_STORE.CREATE);
  const canUpdate = matchPermissionsForUser(PERMISSIONS.MEDICAL_STORE.UPDATE);
  const canDelete = matchPermissionsForUser(PERMISSIONS.MEDICAL_STORE.DELETE);
  const canExport = matchPermissionsForUser(PERMISSIONS.MEDICAL_STORE.EXPORT);

  const nameSearchProps = TableColumnSearch1({
    dataIndex: 'name',
    queryValue: Query,
    onChange: v => setQuery(v),
  });

  const mergedArrow = useMemo(() => {
    if (arrowAtCenter)
      return {
        pointAtCenter: true,
      };
    return showArrow;
  }, [showArrow, arrowAtCenter]);

  const Filters = generateQueryString(
    ['page', Query.page || 1],
    ['patientName', Query.name],
    ['medicalStoreStartDate', Query.medicalStoreStartDate],
    ['medicalStoreEndDate', Query.medicalStoreEndDate]
  );

  const handleResetFilters = () => {
    setQuery({
      ...Query,
      medicalStoreStartDate: '',
      medicalStoreEndDate: '',
    });
  };

  useEffect(() => {
    if (canView) {
      dispatch(ActionGetAllMedicalDetail({ Query: `?page=${params?.page || 1}` }));
      setRefresh(false);
    }
  }, [Refresh, params?.page, canView]);

  useEffect(() => {
    dispatch(ActionGetAllMedicalDetail({ Query: `${Filters}` }));
  }, [dispatch, Filters, Query]);

  // Don't render if user doesn't have view permission
  if (!canView) {
    return (
      <div className="main-wrapper">
        <Header />
        <Sidebar />
        <div className="page-wrapper">
          <div className="content container-fluid">
            <div className="page-header">
              <div className="row">
                <div className="col-sm-12">
                  <h3 className="page-title">Access Denied</h3>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-sm-12">
                <div className="card">
                  <div className="card-body">
                    <p>You don&apos;t have permission to view Medical Store data.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleSearch = (selectedKeys, dataIndex) => {
    setSearchText(selectedKeys?.[0]);
    setSearchedColumn(dataIndex);

    if (params[dataIndex]) {
      window.location.replace(
        `/medical-store?page=1&filterlist=true&${dataIndex}=${selectedKeys?.[0]}`
      );
    } else {
      window.location.replace(
        `/medical-store?${location.search}&${dataIndex}=${selectedKeys?.[0]}`
      );
    }
  };
  const handleReset = clearFilters => {
    clearFilters();
    setSearchText('');
    window.location.replace(`/medical-store`);
  };

  const content = item => (
    <div className="text-center">
      {canUpdate && (
        <Button className="m-2" onClick={() => EditList(item?.id)}>
          Update
        </Button>
      )}
    </div>
  );
  const column = [
    {
      title: 'Sr',
      key: 'sr',
      width: '80px',
      render: (text, record, index) => {
        const currentPage = parseInt(params?.page) || 1;
        const perPage = parseInt(params?.perPage) || 10;
        const calculatedIndex = (currentPage - 1) * perPage + index + 1;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span>{calculatedIndex}</span>
            {record?.appointmentId?.isSimulation && (
              <Tooltip title="Booking is done by simulation mode" placement="top">
                <img
                  src={PrakurtiCirculerLogo}
                  alt={`appointment-${calculatedIndex}`}
                  style={{
                    width: '18px',
                    height: '18px',
                    borderRadius: '50%',
                    objectFit: 'cover',
                    cursor: 'pointer',
                    boxShadow: '0 0 4px rgba(0,0,0,0.2)',
                  }}
                />
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: 'Patient',
      dataIndex: 'name',
      render: (text, record) => <span>{record?.customerId?.name}</span>,
      ...nameSearchProps,
    },
    {
      title: 'Consultation Type',
      dataIndex: 'consultationType',
      render: (text, record) => <span>{record?.appointmentId?.consultationType}</span>,
    },
    {
      title: 'OldCasePaperNo.',
      dataIndex: 'oldCasePaperNo',
      render: (text, record) => <span>{record?.customerId?.oldCasePaperNo}</span>,
    },
    {
      title: 'NewCasePaperNo.',
      dataIndex: 'registrationNo',
      render: (text, record) => <span>{record?.customerId?.registrationNo}</span>,
    },
    {
      title: 'Recomended Medicines',
      dataIndex: 'recomendedMedicines',
      render: (text, record) => <span>{record?.recomendedMedicines[0]?.medicineName}</span>,
    },
    {
      title: 'Days',
      dataIndex: 'days',
      render: (text, record) => <span>{record?.recomendedMedicines[0]?.days}</span>,
    },
    {
      title: 'When to Take',
      dataIndex: 'whenToTake',
      render: (text, record) => <span>{record?.recomendedMedicines[0]?.whenToTake.join('-')}</span>,
    },
    {
      title: !canUpdate ? null : 'Action',
      dataIndex: 'id',
      key: 'action',
      render: (text, record, num) => {
        if (canUpdate) {
          return (
            <Popover
              placement="left"
              title={record.title}
              content={() => content(record)}
              arrow={mergedArrow}
            >
              <Button type="dashed">• • •</Button>
            </Popover>
          );
        }
      },
    },
  ];

  const handleSearch1 = searchTerm => {
    var newp = params;
    newp.page = searchTerm;
    window.location.href = window.location.pathname + '?' + queryString.stringify(newp);
  };
  const EditList = searchTerm => {
    window.location.href = `/medical-store/edit-medical-store?update=true&&id=${searchTerm}&&page=${parseInt(params.page)}`;
  };
  return (
    <>
      <Header />
      <Sidebar />
      <>
        <div>
          <div className="page-wrapper">
            <div className="content">
              {/* Page Header */}
              <div className="page-header">
                <div className="row">
                  <div className="col-sm-12">
                    <ul className="breadcrumb">
                      <li className="breadcrumb-item">
                        <Link to="/medical-store">Medical Store</Link>
                      </li>
                      <li className="breadcrumb-item">
                        <i className="feather-chevron-right">
                          <FeatherIcon icon="chevron-right" />
                        </i>
                      </li>
                      <li className="breadcrumb-item active">Medical Store List</li>
                    </ul>
                  </div>
                </div>
              </div>
              {/* /Page Header */}
              <div className="row">
                <div className="col-sm-12">
                  <div className="card card-table show-entire">
                    <div className="card-body">
                      {/* Table Header */}
                      <div className="page-table-header mb-2">
                        <div className="row align-items-center">
                          <div className="col">
                            <div className="doctor-table-blk">
                              <h3>Medical Store List</h3>
                              <div className="doctor-search-blk">
                                <div className="add-group">
                                  <Link
                                    onClick={() => window.location.reload()}
                                    className="btn btn-primary doctor-refresh ms-2"
                                  >
                                    <img src={refreshicon} alt="#" />
                                  </Link>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div
                          className="col-12 col-lg-6 col-xl-6 mt-3 mb-md-0"
                          style={{ display: 'flex', alignItems: 'center' }}
                        >
                          {/* <DatePicker.RangePicker
                            onChange={dates => {
                              setQuery({
                                medicalStoreStartDate: dates[0]?.format('YYYY-MM-DD') || '',
                                medicalStoreEndDate: dates[1]?.format('YYYY-MM-DD') || '',
                              });
                            }}
                            allowClear={false}
                          /> */}

                          <DatePicker.RangePicker
                            value={
                              Query.medicalStoreStartDate && Query.medicalStoreEndDate
                                ? [
                                  dayjs(Query.medicalStoreStartDate),
                                  dayjs(Query.medicalStoreEndDate)
                                ]
                                : null
                            }
                            onChange={dates => {
                              setQuery({
                                ...Query, // Important: spread existing Query
                                medicalStoreStartDate: dates?.[0]?.format('YYYY-MM-DD') || '',
                                medicalStoreEndDate: dates?.[1]?.format('YYYY-MM-DD') || '',
                              });
                            }}
                            allowClear={false}
                          />

                          <Button type="link" onClick={handleResetFilters} style={{ marginLeft: '10px' }}>
                            Clear Filters
                          </Button>
                        </div>

                      </div>

                      {/* /Table Header */}
                      <CustomTable
                        CurrentPageNum={parseInt(params?.page) || 1}
                        OnChange={v => {
                          handleSearch1(v);
                        }}
                        TotalPageList={medicalStore?.totalDocs}
                        column={column}
                        dataSource={medicalStore?.docs}
                        dataSourceLength={medicalStore?.docs?.length}
                        pageSize={10}
                        loader={loader}
                        ShowHeader={true}
                        Header={<div className="staff-search-table"></div>}
                        key={'hi'}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    </>
  );
};

export default MedicalStore;